<template>
    <v-dialog v-model="dialogValue" max-width="640" :fullscreen="isMobile">
        <DialogLayout title="Not for you?" subtitle="Help us understand (no hard feelings)" card-class="landing-dialog-card" @close="close">
            <v-alert v-if="hasSubmitted" type="success" variant="outlined"> Thanks — your feedback was sent. </v-alert>

            <template v-else>
                <v-form class="pt-2" @submit.prevent="submit">
                    <v-row>
                        <v-col cols="12">
                            <v-select
                                v-model="reason"
                                :disabled="submitting"
                                :error-messages="reasonError ? [reasonError] : []"
                                :items="reasonOptions"
                                density="compact"
                                label="Reason"
                                placeholder="Select one"
                                required
                                variant="outlined" />
                        </v-col>

                        <v-col cols="12">
                            <v-select
                                v-model="discoverySource"
                                :disabled="submitting"
                                :items="discoverySourceOptions"
                                clearable
                                density="compact"
                                label="How did you find this page?"
                                placeholder="Select one"
                                variant="outlined" />
                        </v-col>

                        <v-col cols="12">
                            <v-textarea
                                v-model="feedback"
                                :disabled="submitting"
                                :error-messages="feedbackError ? [feedbackError] : []"
                                auto-grow
                                density="compact"
                                :label="feedbackLabel"
                                :hint="feedbackHint"
                                persistent-hint
                                placeholder="What were you hoping to find instead?"
                                rows="4"
                                variant="outlined" />
                        </v-col>

                        <v-col cols="12">
                            <v-text-field
                                v-model="email"
                                :disabled="submitting"
                                :error-messages="emailError ? [emailError] : []"
                                autocomplete="email"
                                density="compact"
                                hint="Optional. Only if you are okay with a follow-up. No marketing."
                                label="Email (optional)"
                                persistent-hint
                                placeholder="you@bakery.com"
                                type="email"
                                variant="outlined" />
                        </v-col>
                    </v-row>
                </v-form>
            </template>

            <template #actions>
                <v-spacer />
                <v-btn variant="text" :disabled="submitting" @click="close">
                    {{ hasSubmitted ? 'Close' : 'Cancel' }}
                </v-btn>
                <v-btn v-if="!hasSubmitted" color="primary" variant="text" :disabled="!canSubmit" :loading="submitting" @click="submit"> Send </v-btn>
            </template>
        </DialogLayout>
    </v-dialog>
</template>

<script lang="ts" setup>
    import DialogLayout from '@/components/global/DialogLayout.vue';
    import { useNotifications } from '@/composables/useNotifications';
    import { useLandingIsMobile } from '@/pages/Landing/composables/useLandingIsMobile';
    import { validateOptionalEmail } from '@/pages/Landing/utils/landingValidators';
    import { landingPageNotForYouFeedbackService } from '@/services/LandingPageNotForYouFeedbackService';
    import { computed, ref, watch } from 'vue';

    const reasonOptions = [
        'I’m not the target customer',
        'I don’t understand what it does',
        'Too expensive / pricing unclear',
        'Missing a feature I need',
        'Trust / privacy concerns',
        'Just browsing',
        'Other',
    ] as const;

    const discoverySourceOptions = [
        'Google search',
        'Facebook / Instagram',
        'Reddit',
        'Friend / word of mouth',
        'App directory / marketplace',
        'Other',
    ] as const;

    const props = defineProps<{
        modelValue: boolean;
    }>();

    const emit = defineEmits<{
        (e: 'update:modelValue', value: boolean): void;
    }>();

    const dialogValue = computed({
        get: () => props.modelValue,
        set: (value: boolean) => emit('update:modelValue', value),
    });

    const close = () => {
        dialogValue.value = false;
    };

    const { isMobile } = useLandingIsMobile();

    const { success: notifySuccess, error: notifyError } = useNotifications();

    const reason = ref<(typeof reasonOptions)[number] | null>(null);
    const discoverySource = ref<(typeof discoverySourceOptions)[number] | null>(null);
    const feedback = ref('');
    const email = ref('');
    const submitting = ref(false);
    const hasSubmitted = ref(false);

    const reasonError = ref<string | null>(null);
    const feedbackError = ref<string | null>(null);
    const emailError = ref<string | null>(null);

    const validateReason = (value: typeof reason.value): string | null => {
        if (!value) {
            return 'Reason is required.';
        }

        return null;
    };

    const validateFeedback = (reasonValue: typeof reason.value, value: string): string | null => {
        const trimmed = value.trim();

        if (reasonValue === 'Other' && !trimmed) {
            return 'Feedback is required when reason is Other.';
        }

        if (trimmed.length > 5000) {
            return 'Feedback is too long.';
        }

        return null;
    };

    const feedbackLabel = computed(() => (reason.value === 'Other' ? 'Feedback *' : 'Feedback'));
    const feedbackHint = computed(() => (reason.value === 'Other' ? 'Required when reason is Other.' : ''));

    const canSubmit = computed(() => {
        return (
            !submitting.value &&
            validateReason(reason.value) === null &&
            validateFeedback(reason.value, feedback.value) === null &&
            validateOptionalEmail(email.value) === null
        );
    });

    const resetState = () => {
        reason.value = null;
        discoverySource.value = null;
        feedback.value = '';
        email.value = '';
        submitting.value = false;
        hasSubmitted.value = false;
        reasonError.value = null;
        feedbackError.value = null;
        emailError.value = null;
    };

    const submit = async () => {
        reasonError.value = validateReason(reason.value);
        feedbackError.value = validateFeedback(reason.value, feedback.value);
        emailError.value = validateOptionalEmail(email.value);

        if (reasonError.value || feedbackError.value || emailError.value) {
            return;
        }

        submitting.value = true;

        try {
            const normalizedFeedback = feedback.value.trim() ? feedback.value.trim() : null;
            const normalizedEmail = email.value.trim() ? email.value.trim() : null;

            await landingPageNotForYouFeedbackService.submit({
                reason: reason.value!,
                discovery_source: discoverySource.value,
                feedback: normalizedFeedback,
                email: normalizedEmail,
            });

            hasSubmitted.value = true;
            notifySuccess('Feedback sent!');
        } catch (err: any) {
            notifyError(err?.response?.data?.message ?? 'Unable to send your feedback. Please try again.');
        } finally {
            submitting.value = false;
        }
    };

    watch(dialogValue, (open) => {
        if (open) {
            resetState();
        }
    });
</script>

<style lang="scss" scoped></style>
